import { useState } from 'react';
import Drawer from '../Drawer/Drawer';
import StatusBadge from '../StatusBadge/StatusBadge';
import { updateBatchStatus } from '../../lib/directus';
import styles from './BatchDetailDrawer.module.css';

export default function BatchDetailDrawer({ batch, onClose, onAcceptLock, onPaused }) {
  const [pausing, setPausing] = useState(false);
  const isOpen = !!batch;
  const { status } = batch || {};

  const isOrderView = status === 'Awaiting Payment' || status === 'Locked';
  const isPaused    = status === 'paused' || status === 'Paused';

  async function handlePauseToggle() {
    setPausing(true);
    try {
      await updateBatchStatus(batch.id, isPaused ? 'Approved' : 'paused');
      onPaused && onPaused();
      onClose();
    } finally {
      setPausing(false);
    }
  }

  const footer = isOrderView ? (
    <button
      className={`${styles.btnAccept} ${status === 'Locked' ? styles.btnAcceptActive : ''}`}
      onClick={status === 'Locked' ? onAcceptLock : undefined}
      disabled={status !== 'Locked'}
    >
      Accept and lock stock
    </button>
  ) : (
    <>
      <button className={styles.btnEdit}>Edit pricing</button>
      <button className={styles.btnPause} disabled={pausing} onClick={handlePauseToggle}>
        {pausing
          ? (isPaused ? 'Unpausing...' : 'Pausing...')
          : (isPaused ? 'Unpause listing' : 'Pause listing')
        }
      </button>
    </>
  );

  const TIMELINE = [
    { label: 'Batch created',         done: true },
    { label: 'Certificates Uploaded', done: true },
    { label: 'Submitted for Review',  done: true },
    { label: 'Admin Review',          done: status === 'Approved' || status === 'Live', inProgress: status === 'Pending' },
    { label: 'Approved & Listed',     done: status === 'Approved' || status === 'Live' },
  ];

  return (
    <Drawer isOpen={isOpen} title="Batch details" onClose={onClose} footer={batch ? footer : null}>
      {batch && (
        <>
          {/* Status banner for order view */}
          {status === 'Awaiting Payment' && (
            <div className={styles.bannerWarning}>
              <span className={styles.bannerIcon}>⚠</span>
              <div>
                <p className={styles.bannerTitle}>Awaiting Payment Lock</p>
                <p className={styles.bannerSub}>Buyer's finance team is processing</p>
              </div>
            </div>
          )}
          {status === 'Locked' && (
            <div className={styles.bannerSuccess}>
              <span className={styles.bannerIconSuccess}>✓</span>
              <div>
                <p className={styles.bannerTitle}>{batch.escrowAmount} locked in escrow</p>
                <p className={styles.bannerSub}>Verified: {batch.escrowDate}</p>
              </div>
            </div>
          )}

          {/* Main info card */}
          {!isOrderView && (
            <div className={styles.card}>
              <div className={styles.grid}>
                <div>
                  <p className={styles.fieldLabel}>Commodity</p>
                  <p className={styles.fieldValue}>{batch.commodity}</p>
                </div>
                <div>
                  <p className={styles.fieldLabel}>Grade</p>
                  <p className={styles.fieldValue}>{batch.grade}</p>
                </div>
                <div>
                  <p className={styles.fieldLabel}>Quantity</p>
                  <p className={styles.fieldValue}>{batch.quantity}</p>
                </div>
                <div>
                  <p className={styles.fieldLabel}>Warehouse</p>
                  <p className={styles.fieldValue}>{batch.warehouse}</p>
                </div>
                <div>
                  <p className={styles.fieldLabel}>Status</p>
                  <StatusBadge status={batch.status} />
                </div>
                <div>
                  <p className={styles.fieldLabel}>Amount</p>
                  <p className={`${styles.fieldValue} ${styles.amount}`}>{batch.amount}</p>
                </div>
              </div>
            </div>
          )}

          {/* Buyer card (order view) */}
          {isOrderView && batch.buyer && (
            <div className={styles.card}>
              <p className={styles.fieldLabel}>Buyer</p>
              <p className={styles.buyerCompany}>{batch.buyer.company}</p>
              <p className={styles.buyerTrader}>{batch.buyer.trader}</p>
              <div className={styles.buyerRating}>
                {[1, 2, 3, 4, 5].map(i => (
                  <span key={i} style={{ color: i <= Math.floor(batch.buyer.rating) ? '#f59e0b' : '#d1d5db', fontSize: 16 }}>★</span>
                ))}
                <span className={styles.ratingText}>{batch.buyer.rating} • {batch.buyer.trades} trades</span>
              </div>
            </div>
          )}

          {/* Quality specs */}
          <div className={styles.card}>
            <p className={styles.cardTitle}>Quality specifications</p>
            <div className={styles.grid}>
              {isOrderView ? (
                <>
                  <div>
                    <p className={styles.fieldLabel}>Batch</p>
                    <p className={styles.fieldValue}>{batch.batchId}</p>
                  </div>
                  <div>
                    <p className={styles.fieldLabel}>Commodity</p>
                    <p className={styles.fieldValue}>{batch.commodity}</p>
                  </div>
                  <div>
                    <p className={styles.fieldLabel}>Grade</p>
                    <p className={styles.fieldValue}>{batch.grade?.replace('Grade ', '')}</p>
                  </div>
                  <div>
                    <p className={styles.fieldLabel}>Quantity</p>
                    <p className={styles.fieldValue}>{batch.quantity}</p>
                  </div>
                  <div>
                    <p className={styles.fieldLabel}>Warehouse</p>
                    <p className={styles.fieldValue}>{batch.warehouse}</p>
                  </div>
                  <div>
                    <p className={styles.fieldLabel}>Asking price</p>
                    <p className={`${styles.fieldValue} ${styles.amount}`}>{batch.amount}</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className={styles.fieldLabel}>Moisture Content</p>
                    <p className={styles.fieldValue}>{batch.moisture}</p>
                  </div>
                  <div>
                    <p className={styles.fieldLabel}>Foreign Matter</p>
                    <p className={styles.fieldValue}>{batch.foreign}</p>
                  </div>
                  <div>
                    <p className={styles.fieldLabel}>Broken/Damaged</p>
                    <p className={styles.fieldValue}>{batch.broken}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Progress timeline (not for order view) */}
          {!isOrderView && (
            <div className={styles.card}>
              <p className={styles.cardTitle}>Progress timeline</p>
              <div className={styles.timeline}>
                {TIMELINE.map((step, i) => (
                  <div key={i} className={styles.timelineItem}>
                    <div className={styles.timelineLeft}>
                      {step.done ? (
                        <div className={styles.dotDone}>✓</div>
                      ) : step.inProgress ? (
                        <div className={styles.dotProgress}>○</div>
                      ) : (
                        <div className={styles.dotPending}>○</div>
                      )}
                      {i < TIMELINE.length - 1 && (
                        <div className={`${styles.line} ${step.done ? styles.lineDone : ''}`} />
                      )}
                    </div>
                    <div className={styles.timelineContent}>
                      <p className={`${styles.timelineLabel} ${step.done ? styles.timelineLabelDone : step.inProgress ? styles.timelineLabelProgress : styles.timelineLabelPending}`}>
                        {step.label}
                      </p>
                      <p className={styles.timelineTime}>
                        {step.done ? 'Jan 29, 10:30 AM' : step.inProgress ? 'In progress...' : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
